import { uploadMarkdownImage } from 'http/articleApi';

import { FC, useState } from 'react';

import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  headingsPlugin,
  imagePlugin,
  InsertImage,
  InsertTable,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
  quotePlugin,
  Separator,
  StrikeThroughSupSubToggles,
  tablePlugin,
  toolbarPlugin,
  UndoRedo,
} from '@mdxeditor/editor';
import { Button, Divider } from 'antd';
import Title from 'antd/es/typography/Title';
import { ICreateArticle } from 'common/types';
import { articleSchema } from 'common/validation';
import {
  Form,
  FormInput,
  FormSelect,
  FormSwitch,
  FormMDXEditor,
  FormTextArea,
} from 'components/Form';
import {
  TextAlignmentToggle,
  emptyParagraphPlugin,
  textAlignmentPlugin,
} from 'components/Form/MDXEditor';
import { EditImageToolbar } from 'components/Form/MDXEditor/image/EditImageToolbar';
import { imageFloatPlugin } from 'components/Form/MDXEditor/image/imageFloatPlugin';
import { Spinner } from 'components/Spinner';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';

import { ArticleControllerViewModel } from './article-controller-view-model';

import styles from './article-controller.module.scss';

interface IArticleControllerProps {
  model: ArticleControllerViewModel;
}

const ArticleController: FC<IArticleControllerProps> = observer(({ model }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const article = model.article;

  const isUpdate = !!model.article.id;
  const eventText = isUpdate ? 'Update' : 'Create';

  const onSubmit = async (values: ICreateArticle) => {
    setLoading(true);

    const result = await model.manageArticle(isUpdate, values);

    if (result) {
      navigate({
        pathname: '/articles',
      });
    }

    setLoading(false);
  };

  const cancel = () =>
    navigate({
      pathname: '/articles',
    });

  if (model.loading) {
    return (
      <div className={styles.formLoading}>
        <Spinner type="large" />
      </div>
    );
  }

  async function imageUploadHandler(image: File) {
    const fd = new FormData();

    if (image) {
      fd.append('image', image);
      const newImage = await uploadMarkdownImage(fd);

      return newImage.url;
    }

    return '';
  }

  return (
    <div className={styles.formContainer}>
      <Title level={5} type="secondary" className={styles.title}>
        {`${eventText} article`}
      </Title>
      <Form
        validation={articleSchema}
        initialValues={{
          title: article.title,
          content: article.content,
          slug: article.slug,
          metaTitle: article.metaTitle,
          metaDescription: article.metaDescription,
          metaKeywords: article.metaKeywords,
          published: article.published,
          categoryId: article.categoryId || undefined,
        }}
        onSubmit={onSubmit}
        scrollToError
      >
        <div className={styles.formContent}>
          <div className={styles.fields}>
            <FormInput
              required
              name="title"
              label="Title"
              placeholder="Enter article title"
              className={styles.field}
            />
            <FormMDXEditor
              required
              name="content"
              label="Content"
              className={styles.field}
              plugins={[
                toolbarPlugin({
                  toolbarContents: () => (
                    <>
                      <UndoRedo />
                      <BlockTypeSelect />
                      <BoldItalicUnderlineToggles />
                      <StrikeThroughSupSubToggles options={['Strikethrough']} />
                      <Separator />
                      <TextAlignmentToggle />
                      <Separator />
                      <CreateLink />
                      <InsertImage />
                      <InsertTable />
                      <Separator />
                      <ListsToggle options={['bullet', 'number']} />
                    </>
                  ),
                }),
                tablePlugin(),
                linkPlugin(),
                linkDialogPlugin(),
                imagePlugin({
                  imageUploadHandler,
                  EditImageToolbar,
                }),
                imageFloatPlugin(),
                headingsPlugin({ allowedHeadingLevels: [2, 3, 4, 5, 6] }),
                listsPlugin(),
                quotePlugin(),
                textAlignmentPlugin(),
                markdownShortcutPlugin(),
                emptyParagraphPlugin(),
              ]}
            />
            <FormInput
              name="metaTitle"
              label="Meta title"
              placeholder="Enter meta title"
              className={styles.field}
              showCount
            />
            <FormTextArea
              name="metaDescription"
              label="Meta description"
              placeholder="Enter meta description"
              autoSize={{ minRows: 6, maxRows: 10 }}
              className={styles.field}
              showCount
            />
            <FormInput
              name="metaKeywords"
              placeholder="Enter meta keywords"
              label="Meta keywords"
              className={styles.field}
              showCount
            />
            <FormSelect
              required
              name="categoryId"
              label="Category"
              options={model.categoryOptions}
              placeholder="Article category"
              disabled={model.loading}
            />
            <FormSwitch name="published" label="Published" />
            <FormInput
              className={styles.field}
              label="Slug"
              name="slug"
              disabled
            />
            <Divider />
            <div className={styles.buttons}>
              <Button
                disabled={loading || model.loading}
                onClick={cancel}
                type="default"
              >
                Cancel
              </Button>
              <Button
                loading={loading || model.loading}
                disabled={loading || model.loading}
                type="primary"
                htmlType="submit"
              >
                {eventText}
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
});

export const ArticleControllerPage = () => {
  const { articleId } = useParams();

  const model = new ArticleControllerViewModel(Number(articleId));

  return <ArticleController model={model} />;
};
